import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getEmployees } from "../api";

const NAVY = "#1e3a8a";
const TEAL = "#00c9a7";

const ROLE_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  admin:           { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  admin_assistant: { bg: "#f0fdf4", text: "#166534", border: "#86efac" },
  hr:              { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  recruiter:       { bg: "#fdf4ff", text: "#7e22ce", border: "#e9d5ff" },
  finance:         { bg: "#fef9c3", text: "#854d0e", border: "#fde047" },
  employee:        { bg: "#f1f5f9", text: "#475569", border: "#cbd5e1" },
};

const DEPT_COLORS: Record<string, string> = {
  Engineering: "#3b82f6", Marketing: "#f59e0b", Sales: "#10b981",
  HR: "#8b5cf6", Finance: "#ef4444", Operations: "#64748b",
  Design: "#ec4899", Product: "#f97316",
};

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:30801";

function Avatar({ name, size = 40, userId }: { name: string; size?: number; userId?: number }) {
  const [hasPhoto, setHasPhoto] = React.useState(!!userId);
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const hue = name.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0) % 360;
  if (hasPhoto && userId) {
    return (
      <img
        src={`${API_URL}/api/employees/${userId}/photo`}
        alt={name}
        onError={() => setHasPhoto(false)}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: `hsl(${hue}, 55%, 60%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.35, fontWeight: 700, color: "white", flexShrink: 0,
    }}>{initials}</div>
  );
}

interface Employee {
  id: number; name: string; email: string; role: string;
  designation?: string; department?: string; employee_id?: string;
  manager_id?: number; is_active?: boolean; invite_pending?: boolean;
}

function OrgNode({ emp, reports, depth, expanded, onToggle, onSelect, selected }: {
  emp: Employee; reports: Employee[]; depth: number;
  expanded: boolean; onToggle: () => void; onSelect: () => void; selected: boolean;
}) {
  const rc = ROLE_COLORS[emp.role] || ROLE_COLORS.employee;
  const deptColor = DEPT_COLORS[emp.department || ""] || "#94a3b8";

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      {/* Node card */}
      <div
        onClick={onSelect}
        style={{
          background: "white",
          border: `2px solid ${selected ? NAVY : "#e2e8f0"}`,
          borderRadius: 12,
          padding: "12px 16px",
          minWidth: 180,
          maxWidth: 220,
          cursor: "pointer",
          boxShadow: selected ? `0 0 0 3px ${NAVY}22` : "0 1px 4px rgba(0,0,0,0.06)",
          transition: "all 0.15s",
          position: "relative",
        }}>
        {emp.invite_pending && (
          <div style={{ position: "absolute", top: 6, right: 8, fontSize: 9, background: "#fef9c3", color: "#854d0e", padding: "1px 5px", borderRadius: 8, fontWeight: 700 }}>PENDING</div>
        )}
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 8 }}>
          <Avatar name={emp.name} size={36} userId={emp.id} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{emp.name}</div>
            <div style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{emp.designation || emp.email}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 10, background: rc.bg, color: rc.text, border: `1px solid ${rc.border}` }}>
            {emp.role.replace("_", " ")}
          </span>
          {emp.department && (
            <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px", borderRadius: 10, background: `${deptColor}18`, color: deptColor, border: `1px solid ${deptColor}44` }}>
              {emp.department}
            </span>
          )}
        </div>
        {reports.length > 0 && (
          <button onClick={e => { e.stopPropagation(); onToggle(); }} style={{
            position: "absolute", bottom: -12, left: "50%", transform: "translateX(-50%)",
            width: 22, height: 22, borderRadius: "50%", border: `2px solid ${NAVY}`,
            background: "white", cursor: "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", fontSize: 14, color: NAVY, fontWeight: 700, zIndex: 2,
          }}>{expanded ? "−" : "+"}</button>
        )}
      </div>

      {/* Children */}
      {reports.length > 0 && expanded && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: 28 }}>
          {/* Vertical line down from parent */}
          <div style={{ width: 2, height: 20, background: "#e2e8f0", marginBottom: 0 }} />
          {/* Horizontal bar */}
          {reports.length > 1 && (
            <div style={{ position: "relative", display: "flex", alignItems: "flex-start", gap: 24 }}>
              <div style={{
                position: "absolute", top: 0, left: "50%",
                width: `calc(100% - 110px)`, height: 2, background: "#e2e8f0",
                transform: "translateX(-50%)",
              }} />
              {reports.map(child => (
                <React.Fragment key={child.id}><ChildSubtree emp={child as Employee} /></React.Fragment>
              ))}
            </div>
          )}
          {reports.length === 1 && <ChildSubtree emp={reports[0]} />}
        </div>
      )}
    </div>
  );
}

function ChildSubtree({ emp }: { emp: Employee }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <div style={{ width: 2, height: 20, background: "#e2e8f0" }} />
      <ConnectedSubtree emp={emp} depth={1} />
    </div>
  );
}

function ConnectedSubtree({ emp, depth }: { emp: Employee; depth: number }) {
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: getEmployees });
  const all = employees as Employee[];
  const children = all.filter(e => e.manager_id === emp.id);
  const [expanded, setExpanded] = useState(depth < 2);
  const [selected, setSelected] = useState(false);

  return (
    <OrgNode
      emp={emp}
      reports={children}
      depth={depth}
      expanded={expanded}
      onToggle={() => setExpanded(e => !e)}
      onSelect={() => setSelected(s => !s)}
      selected={selected}
    />
  );
}

export default function OrgChartPage() {
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: getEmployees });
  const all = employees as Employee[];
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [view, setView] = useState<"chart" | "list">("chart");

  const departments = useMemo(() =>
    Array.from(new Set(all.map(e => e.department).filter(Boolean))), [all]);

  // Roots = employees with no manager OR whose manager is in a different tenant
  const roots = useMemo(() => {
    const ids = new Set(all.map(e => e.id));
    return all.filter(e => !e.manager_id || !ids.has(e.manager_id));
  }, [all]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return all.filter(e =>
      (!q || e.name.toLowerCase().includes(q) || (e.designation || "").toLowerCase().includes(q) || (e.department || "").toLowerCase().includes(q)) &&
      (!deptFilter || e.department === deptFilter)
    );
  }, [all, search, deptFilter]);

  const deptStats = useMemo(() => {
    const map: Record<string, number> = {};
    all.forEach(e => { if (e.department) map[e.department] = (map[e.department] || 0) + 1; });
    return map;
  }, [all]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ margin: 0, color: "#0f172a", fontSize: 24, fontWeight: 700 }}>Org Chart</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
            {all.length} team members across {departments.length} departments
          </p>
        </div>
      </div>

      {/* Stats bar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        {Object.entries(deptStats).map(([dept, count]) => (
          <div key={dept} onClick={() => setDeptFilter(deptFilter === dept ? "" : dept)} style={{
            padding: "6px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600,
            background: deptFilter === dept ? NAVY : "white",
            color: deptFilter === dept ? "white" : (DEPT_COLORS[dept] || "#64748b"),
            border: `1.5px solid ${deptFilter === dept ? NAVY : (DEPT_COLORS[dept] || "#e2e8f0")}`,
          }}>
            {dept} <span style={{ opacity: 0.7 }}>({count})</span>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: 10, marginBottom: 24, alignItems: "center", flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, designation, department…"
          style={{ flex: "1 1 260px", padding: "9px 14px", borderRadius: 8, border: "1.5px solid #e2e8f0", fontSize: 13, color: "#1e293b", outline: "none" }} />
        <div style={{ display: "flex", border: "1.5px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
          {(["chart", "list"] as const).map(v => (
            <button key={v} onClick={() => setView(v)} style={{
              padding: "8px 16px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
              background: view === v ? NAVY : "white",
              color: view === v ? "white" : "#64748b",
            }}>{v === "chart" ? "🌳 Chart" : "☰ List"}</button>
          ))}
        </div>
      </div>

      {/* Chart view */}
      {view === "chart" && !search && !deptFilter && (
        <div style={{ overflowX: "auto", paddingBottom: 40 }}>
          <div style={{ display: "flex", gap: 40, justifyContent: "center", minWidth: "max-content", padding: "20px 40px" }}>
            {roots.map(root => (
              <React.Fragment key={root.id}><ConnectedSubtree emp={root} depth={0} /></React.Fragment>
            ))}
          </div>
          {roots.length === 0 && (
            <div style={{ textAlign: "center", padding: "80px 0", color: "#94a3b8" }}>
              <div style={{ fontSize: 48 }}>🌳</div>
              <div style={{ marginTop: 12 }}>No employees yet.</div>
            </div>
          )}
        </div>
      )}

      {/* List view or filtered */}
      {(view === "list" || search || deptFilter) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 14 }}>
          {filtered.map(emp => {
            const rc = ROLE_COLORS[emp.role] || ROLE_COLORS.employee;
            const deptColor = DEPT_COLORS[emp.department || ""] || "#94a3b8";
            const manager = all.find(e => e.id === emp.manager_id);
            const directReports = all.filter(e => e.manager_id === emp.id).length;
            return (
              <div key={emp.id} style={{
                background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16,
              }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
                  <Avatar name={emp.name} size={44} userId={emp.id} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>{emp.name}</div>
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>{emp.designation || emp.email}</div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: rc.bg, color: rc.text, border: `1px solid ${rc.border}` }}>
                    {emp.role.replace("_", " ")}
                  </span>
                  {emp.department && (
                    <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: `${deptColor}18`, color: deptColor, border: `1px solid ${deptColor}44` }}>
                      {emp.department}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "#64748b" }}>
                  {emp.employee_id && <div>ID: <b>{emp.employee_id}</b></div>}
                  {manager && <div>Reports to: <b>{manager.name}</b></div>}
                  {directReports > 0 && <div style={{ color: NAVY }}>Manages <b>{directReports}</b> {directReports === 1 ? "person" : "people"}</div>}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "60px 0", color: "#94a3b8" }}>
              No employees match your search.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
